/**
 * Admin Leads Management Component
 * View and manage leads captured from ebook downloads and other funnels
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Users,
  Search,
  Download,
  Mail,
  Phone,
  Calendar,
  Tag,
  ExternalLink,
  RefreshCw,
  Loader2,
  BookOpen,
  Globe,
  ArrowRight,
  CheckCircle2,
  Clock,
  Filter,
  Send,
  Sparkles,
  Plus,
  X,
  UserCheck,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  'homepage_popup': { label: 'Homepage Popup', color: 'bg-purple-100 text-purple-700', icon: Globe },
  'ebook_landing': { label: 'Ebook Landing Page', color: 'bg-blue-100 text-blue-700', icon: BookOpen },
  'referral': { label: 'Referral', color: 'bg-green-100 text-green-700', icon: Users },
  'social': { label: 'Social Media', color: 'bg-pink-100 text-pink-700', icon: Sparkles },
  'unknown': { label: 'Direct', color: 'bg-gray-100 text-gray-700', icon: Globe }
};

const LEAD_MAGNET_LABELS = {
  'whats_your_next_best_move_ebook': "What's Your Next Best Move? Ebook",
  'other': 'Other'
};

const TAG_LABELS = {
  'ebook_download': { label: 'Ebook Download', color: 'bg-indigo-100 text-indigo-700' },
  'newsletter_signup': { label: 'Newsletter', color: 'bg-blue-100 text-blue-700' },
  'vip_interest': { label: 'VIP Interest', color: 'bg-amber-100 text-amber-700' },
  'retreat_interest': { label: 'Retreat Interest', color: 'bg-teal-100 text-teal-700' },
  'coaching_interest': { label: 'Coaching Interest', color: 'bg-rose-100 text-rose-700' },
  'collective_interest': { label: 'Collective Interest', color: 'bg-purple-100 text-purple-700' },
  'free_consultation': { label: 'Free Consultation', color: 'bg-green-100 text-green-700' },
  'webinar_attendee': { label: 'Webinar Attendee', color: 'bg-orange-100 text-orange-700' }
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700' },
  { value: 'nurturing', label: 'Nurturing', color: 'bg-purple-100 text-purple-700' },
  { value: 'converted', label: 'Converted', color: 'bg-green-100 text-green-700' },
  { value: 'unqualified', label: 'Unqualified', color: 'bg-gray-100 text-gray-700' }
];

const AdminLeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [sendingToOntraport, setSendingToOntraport] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/leads`);
      setLeads(response.data.leads || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API_URL}/api/admin/leads/${leadId}`, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      toast.success('Lead status updated');
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const sendToOntraport = async (lead) => {
    setSendingToOntraport(true);
    try {
      await axios.post(`${API_URL}/api/admin/leads/${lead.id}/send-to-ontraport`);
      toast.success('Lead sent to Ontraport');
      // Update local state
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, sent_to_ontraport: true } : l));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to Ontraport');
    } finally {
      setSendingToOntraport(false);
    }
  };

  const exportLeads = () => {
    const filteredLeads = getFilteredLeads();
    const headers = ['Name', 'Email', 'Phone', 'Source', 'Lead Magnet', 'Status', 'Created At'];
    const rows = filteredLeads.map(l => [
      `${l.first_name || ''} ${l.last_name || ''}`.trim(),
      l.email,
      l.phone || '',
      l.source || 'unknown',
      l.lead_magnet || '',
      l.status || 'new',
      l.created_at ? new Date(l.created_at).toLocaleDateString() : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLeads.length} leads`);
  };

  const getFilteredLeads = () => {
    return leads.filter(lead => {
      const matchesSearch = !searchTerm || 
        lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSource = !sourceFilter || lead.source === sourceFilter;
      const matchesStatus = !statusFilter || lead.status === statusFilter;
      
      return matchesSearch && matchesSource && matchesStatus;
    });
  };

  const filteredLeads = getFilteredLeads();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getSourceInfo = (source) => {
    return SOURCE_LABELS[source] || SOURCE_LABELS['unknown'];
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-forest">{stats.total || 0}</p>
          <p className="text-sm text-gray-500">Total Leads</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.new || 0}</p>
          <p className="text-sm text-gray-500">New</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.contacted || 0}</p>
          <p className="text-sm text-gray-500">Contacted</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{stats.nurturing || 0}</p>
          <p className="text-sm text-gray-500">Nurturing</p>
        </Card>
        <Card className="p-4 text-center bg-emerald-50 border-emerald-200">
          <p className="text-2xl font-bold text-emerald-600">{stats.converted || 0}</p>
          <p className="text-sm text-emerald-600">Converted</p>
        </Card>
        <Card className="p-4 text-center bg-indigo-50 border-indigo-200">
          <div>
            <p className="text-2xl font-bold text-indigo-600">{stats.conversion_rate || 0}%</p>
            <p className="text-sm text-indigo-600">Conv. Rate</p>
          </div>
          {stats.avg_days_to_convert > 0 && (
            <p className="text-xs text-indigo-400 mt-1">~{stats.avg_days_to_convert} days avg</p>
          )}
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-forest" />
                Leads ({filteredLeads.length})
              </CardTitle>
              <CardDescription>Manage leads from ebook downloads and other funnels</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchLeads} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" onClick={exportLeads}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={sourceFilter || 'all'} onValueChange={(v) => setSourceFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {Object.entries(SOURCE_LABELS).map(([key, value]) => (
                  <SelectItem key={key} value={key}>{value.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Leads List */}
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-forest" />
              <p className="text-gray-500 mt-2">Loading leads...</p>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No leads found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLeads.map((lead) => {
                const sourceInfo = getSourceInfo(lead.source);
                const statusInfo = getStatusInfo(lead.status);
                const SourceIcon = sourceInfo.icon;
                
                return (
                  <div 
                    key={lead.id} 
                    className="p-4 border rounded-xl hover:border-forest/50 transition-colors cursor-pointer"
                    onClick={() => { setSelectedLead(lead); setShowLeadDialog(true); }}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-forest">
                            {lead.first_name} {lead.last_name}
                          </h3>
                          <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          {lead.sent_to_ontraport && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Ontraport
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {lead.email}
                          </span>
                          {lead.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {lead.phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(lead.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Converted badge - show prominently if converted */}
                        {lead.status === 'converted' && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                            <UserCheck className="h-3 w-3 mr-1" />
                            Converted
                            {lead.converted_to_tier && ` → ${lead.converted_to_tier}`}
                          </Badge>
                        )}
                        <Badge className={sourceInfo.color}>
                          <SourceIcon className="h-3 w-3 mr-1" />
                          {sourceInfo.label}
                        </Badge>
                        {lead.lead_magnet && (
                          <Badge variant="outline">
                            <BookOpen className="h-3 w-3 mr-1" />
                            Ebook
                          </Badge>
                        )}
                        {/* Display tags */}
                        {lead.tags?.length > 0 && lead.tags.slice(0, 2).map(tag => (
                          <Badge key={tag} className={TAG_LABELS[tag]?.color || 'bg-gray-100 text-gray-700'}>
                            <Tag className="h-3 w-3 mr-1" />
                            {TAG_LABELS[tag]?.label || tag}
                          </Badge>
                        ))}
                        {lead.tags?.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{lead.tags.length - 2} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>
              View and manage this lead
            </DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-4 py-4">
              {/* Contact Info */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Name</Label>
                  <p className="font-medium">{selectedLead.first_name} {selectedLead.last_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <p className="font-medium">{selectedLead.email}</p>
                </div>
                {selectedLead.phone && (
                  <div>
                    <Label className="text-xs text-gray-500">Phone</Label>
                    <p className="font-medium">{selectedLead.phone}</p>
                  </div>
                )}
              </div>
              
              {/* Source & Lead Magnet */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Source</Label>
                  <Badge className={getSourceInfo(selectedLead.source).color + ' mt-1'}>
                    {getSourceInfo(selectedLead.source).label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Lead Magnet</Label>
                  <p className="text-sm mt-1">{LEAD_MAGNET_LABELS[selectedLead.lead_magnet] || selectedLead.lead_magnet || 'None'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Created</Label>
                  <p className="text-sm mt-1">{formatDate(selectedLead.created_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Ontraport</Label>
                  <p className="text-sm mt-1">
                    {selectedLead.sent_to_ontraport ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Synced
                      </span>
                    ) : (
                      <span className="text-gray-400">Not synced</span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Tags Section */}
              <div className="p-4 bg-indigo-50 rounded-lg">
                <Label className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <Tag className="h-3 w-3" /> Tags
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedLead.tags?.length > 0 ? (
                    selectedLead.tags.map(tag => (
                      <Badge key={tag} className={TAG_LABELS[tag]?.color || 'bg-gray-100 text-gray-700'}>
                        {TAG_LABELS[tag]?.label || tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No tags assigned</span>
                  )}
                </div>
              </div>
              
              {/* Conversion Info (if converted) */}
              {selectedLead.status === 'converted' && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Label className="text-xs text-emerald-600 mb-2 flex items-center gap-1">
                    <UserCheck className="h-3 w-3" /> Conversion Details
                  </Label>
                  <div className="space-y-2 mt-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Converted to:</span>
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {selectedLead.converted_to_tier || 'Member'}
                      </Badge>
                    </div>
                    {selectedLead.converted_at && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">Converted on:</span>
                        <span className="font-medium">{formatDate(selectedLead.converted_at)}</span>
                      </div>
                    )}
                    {selectedLead.days_to_convert !== undefined && selectedLead.days_to_convert !== null && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">Time to convert:</span>
                        <span className="font-medium text-emerald-700">
                          {selectedLead.days_to_convert} {selectedLead.days_to_convert === 1 ? 'day' : 'days'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Status Update */}
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Status</Label>
                <Select 
                  value={selectedLead.status || 'new'} 
                  onValueChange={(v) => updateLeadStatus(selectedLead.id, v)}
                  disabled={updatingStatus}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-4">
                {!selectedLead.sent_to_ontraport && (
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => sendToOntraport(selectedLead)}
                    disabled={sendingToOntraport}
                  >
                    {sendingToOntraport ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send to Ontraport
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => window.open(`mailto:${selectedLead.email}`, '_blank')}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeadsManagement;
